/**
 * Mensajes seguros para usuarios finales (sin detalles técnicos de DB, Prisma, red, etc.).
 */
export const EXTERNAL_ISSUES_MESSAGE =
  "Estamos experimentando inconvenientes con nuestros proveedores. En breve lo normalizaremos. Por favor, intentá de nuevo más tarde.";

const TECHNICAL_PATTERNS: RegExp[] = [
  /prisma/i,
  /invalid\s+`?prisma/i,
  /can't reach database/i,
  /database server/i,
  /\bp100[0-9]\b/i,
  /econnrefused|etimedout|enotfound|eai_again/i,
  /neon\.tech/i,
  /postgresql/i,
  /invocation:/i,
  /unique constraint/i,
  /foreign key constraint/i,
  /sqlstate/i,
  /internal server error/i,
  /unexpected token/i,
  /syntaxerror|typeerror|referenceerror/i,
];

/** Tabla/columna faltante tras un deploy sin migración aplicada. */
export function isPrismaSchemaDriftError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const code = (err as { code?: string }).code;
  const msg = err instanceof Error ? err.message : String(err);
  return code === "P2021" || code === "P2022" || /does not exist in the current database/i.test(msg);
}

export function isTechnicalErrorMessage(message: string): boolean {
  const m = message.trim();
  if (!m) return false;
  if (m.length > 160 && /\bat\s+\S+\s+\(/.test(m)) return true;
  return TECHNICAL_PATTERNS.some((re) => re.test(m));
}

/**
 * Extrae el mensaje legible de cualquier valor lanzado como error: instancias de `Error`,
 * strings, o los objetos JSON crudos que tiran algunos SDKs en vez de un `Error` real
 * (ej: el SDK de Mercado Pago hace `throw await response.json()` en sus clientes REST,
 * así que el error de la API llega como `{ message, error, cause: [...] }` plano, no
 * como `instanceof Error`). Sin esto, ese mensaje se pierde y siempre cae al fallback.
 */
function extractRawMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "string") return err;
  if (err && typeof err === "object") {
    const o = err as Record<string, unknown>;
    if (typeof o.message === "string" && o.message.trim()) return o.message;
    if (typeof o.error === "string" && o.error.trim()) return o.error;
    if (Array.isArray(o.cause) && o.cause.length > 0) {
      const first = o.cause[0] as Record<string, unknown> | undefined;
      if (typeof first?.description === "string" && first.description.trim()) return first.description;
    }
  }
  return "";
}

/** Texto para mostrar en UI o devolver en JSON `{ error }`. */
export function clientSafeErrorMessage(
  err: unknown,
  options?: { fallback?: string },
): string {
  const raw = extractRawMessage(err);
  if (!raw || isTechnicalErrorMessage(raw)) {
    return options?.fallback ?? EXTERNAL_ISSUES_MESSAGE;
  }
  return raw;
}

export function clientSafeHttpStatus(err: unknown, fallback = 503): number {
  const raw = extractRawMessage(err);
  if (isTechnicalErrorMessage(raw)) return fallback;

  if (err && typeof err === "object" && "status" in err) {
    const s = Number((err as { status: number }).status);
    if (s >= 400 && s < 600) return s;
  }
  return fallback;
}

/** Respuesta JSON segura para rutas Express. */
export function respondClientError(
  res: import("express").Response,
  err: unknown,
  opts?: { logLabel?: string },
): void {
  if (opts?.logLabel) console.error(`[${opts.logLabel}]`, err);
  else console.error(err);

  const status = clientSafeHttpStatus(err);
  res.status(status).json({ error: clientSafeErrorMessage(err) });
}

/** Capa extra en el front por si alguna respuesta antigua trae texto técnico. */
export function displayApiError(
  apiError: string | undefined | null,
  fallback = EXTERNAL_ISSUES_MESSAGE,
): string {
  if (!apiError?.trim()) return fallback;
  if (isTechnicalErrorMessage(apiError)) return EXTERNAL_ISSUES_MESSAGE;
  return apiError;
}
